<script setup lang="ts">
/**
 * A lineage at a glance: its topology (environments, systems, resources), what
 * belongs to it by type, who owns it, what changed recently and which
 * documents mention it. One aggregate call plus the topology graph and the
 * change feed filtered to the lineage.
 */
import { Activity, BookOpen, FileText, Maximize2, Network, Search, SearchX, Users, Waypoints } from "@lucide/vue";
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { isProblem } from "@/api/client";
import { isGroupNode, type GraphNode } from "@/api/contracts";
import { useGraph, useKnowledgeChanges, useLineageOverview } from "@/api/knowledge";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import SectionCard from "@/components/common/SectionCard.vue";
import GraphCanvas from "@/components/graph/GraphCanvas.vue";
import ChangeFeedItem from "@/components/knowledge/ChangeFeedItem.vue";
import EntityLink from "@/components/knowledge/EntityLink.vue";
import KnowledgeNav from "@/components/knowledge/KnowledgeNav.vue";
import TypeBadge from "@/components/knowledge/TypeBadge.vue";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { t } from "@/copy/strings";
import { formatNumber } from "@/lib/format";
import { ENTITY_TYPES, entityPath, typeInfo } from "@/lib/knowledge";

definePage({ meta: { title: "Lineage", requires: { capability: "knowledge.read" } } });

const route = useRoute("/knowledge/lineage/[slug]");
const router = useRouter();
const slug = computed(() => route.params.slug);

const overview = useLineageOverview(slug);
const data = computed(() => overview.data.value);
const notFound = computed(() => isProblem(overview.error.value) && overview.error.value.status === 404 && !overview.error.value.notAvailable);

const graph = useGraph("topology", () => ({ lineage: slug.value }), () => Boolean(data.value));
const graphUseful = computed(() => (graph.data.value?.nodes.length ?? 0) > 1);
const changes = useKnowledgeChanges(() => ({ lineage: slug.value, limit: 10 }));

/** Member groups in the order of the type list, the lineage's environments first. */
const groups = computed(() =>
  ENTITY_TYPES.filter((x) => x.type !== "lineage" && (data.value?.byType[x.type] ?? 0) > 0).map((x) => ({
    info: x,
    count: data.value!.byType[x.type],
    items: data.value!.members[x.type] ?? [],
  })),
);
const environments = computed(() => data.value?.members.environment ?? []);
const memberGroups = computed(() => groups.value.filter((g) => g.info.type !== "environment"));
const searchLink = (type?: string) => ({ path: "/knowledge/search", query: { lineage: slug.value, ...(type ? { types: type } : {}) } });

function openNode(n: GraphNode) {
  if (!isGroupNode(n) && n.type && n.slug) void router.push(entityPath(n.type, n.slug));
}
</script>

<template>
  <div>
    <ListSkeleton v-if="overview.isPending.value" :rows="6" />
    <EmptyState v-else-if="notFound" :icon="SearchX" :title="`No lineage “${slug}”`" description="Nothing you can see belongs to this lineage.">
      <Button as-child><RouterLink to="/knowledge">{{ t.nav.overview }}</RouterLink></Button>
    </EmptyState>
    <ErrorState v-else-if="overview.error.value" :error="overview.error.value" @retry="overview.refetch()" />

    <template v-else-if="data">
      <PageHeader
        :title="data.name"
        :description="data.entity?.description || undefined"
        :crumbs="[{ label: t.nav.knowledge, to: '/knowledge' }, { label: t.knowledge.lineageMap, to: '/knowledge' }, { label: data.name }]"
      >
        <template #badges><TypeBadge type="lineage" /></template>
        <template #meta>
          <p class="mt-2 text-sm text-muted-foreground">{{ formatNumber(data.total) }} pages in this lineage</p>
        </template>
        <template #actions>
          <Button v-if="data.entity" as-child variant="outline"><RouterLink :to="entityPath(data.entity.type, data.entity.slug)"><BookOpen /> Open page</RouterLink></Button>
          <Button as-child variant="outline"><RouterLink :to="searchLink()"><Search /> Search in {{ data.name }}</RouterLink></Button>
        </template>
      </PageHeader>
      <KnowledgeNav />

      <!-- Counts per type -->
      <ul class="mb-6 flex flex-wrap gap-2" aria-label="Pages by type">
        <li v-for="g in groups" :key="g.info.type">
          <RouterLink
            :to="searchLink(g.info.type)"
            class="inline-flex items-center gap-1.5 rounded-lg border bg-card px-2.5 py-1.5 text-sm transition-colors hover:border-primary/40 hover:bg-accent/30"
          >
            <component :is="g.info.icon" class="size-3.5" :style="{ color: g.info.color }" aria-hidden="true" />
            <span class="font-medium">{{ g.info.plural }}</span>
            <span class="text-xs text-muted-foreground tabular-nums">{{ formatNumber(g.count) }}</span>
          </RouterLink>
        </li>
      </ul>

      <div class="grid gap-6 xl:grid-cols-3">
        <div class="flex min-w-0 flex-col gap-6 xl:col-span-2">
          <SectionCard :title="t.knowledge.lineageTopology" :icon="Waypoints">
            <template #actions>
              <Button as-child variant="ghost" size="icon-sm" aria-label="Open in graph views">
                <RouterLink :to="{ path: '/knowledge/views', query: { view: 'topology', lineage: slug } }"><Maximize2 /></RouterLink>
              </Button>
            </template>
            <Skeleton v-if="graph.isPending.value" class="h-[26rem] w-full" />
            <ErrorState v-else-if="graph.error.value" compact :error="graph.error.value" @retry="graph.refetch()" />
            <EmptyState v-else-if="!graphUseful" compact :icon="Network" title="No topology recorded yet" description="Relations between environments, systems and resources appear here." />
            <template v-else-if="graph.data.value">
              <p v-if="graph.data.value.truncated" class="mb-2 rounded-md bg-warning-soft px-3 py-2 text-sm">Showing the first 60 items. Open the graph views to explore.</p>
              <GraphCanvas
                :nodes="graph.data.value.nodes"
                :edges="graph.data.value.edges"
                :focus-id="graph.data.value.nodes.find((n) => n.type === 'lineage')?.id"
                height="26rem"
                :aria-label="`Topology of ${data.name}`"
                @select="openNode"
              />
            </template>
          </SectionCard>

          <div v-if="memberGroups.length" class="grid gap-4 md:grid-cols-2">
            <SectionCard
              v-for="g in memberGroups"
              :key="g.info.type"
              :title="g.info.plural"
              :icon="g.info.icon"
              :to="g.count > g.items.length ? `/knowledge/search?lineage=${encodeURIComponent(slug)}&types=${g.info.type}` : undefined"
              :link-label="`All ${formatNumber(g.count)}`"
            >
              <ul class="flex flex-col">
                <li v-for="m in g.items" :key="m.uri">
                  <EntityLink :entity="m"><span v-if="m.subtype" class="shrink-0 text-xs text-muted-foreground">{{ m.subtype }}</span></EntityLink>
                </li>
              </ul>
            </SectionCard>
          </div>
        </div>

        <div class="flex min-w-0 flex-col gap-6">
          <SectionCard v-if="environments.length" :title="typeInfo('environment').plural" :icon="typeInfo('environment').icon">
            <ul class="flex flex-col">
              <li v-for="e in environments" :key="e.uri"><EntityLink :entity="e" /></li>
            </ul>
          </SectionCard>

          <SectionCard :title="t.knowledge.lineageOwners" :icon="Users">
            <p v-if="!data.owners.length" class="text-sm text-muted-foreground">{{ t.knowledge.noOwner }}</p>
            <ul v-else class="flex flex-col">
              <li v-for="o in data.owners" :key="o.entity.uri">
                <EntityLink :entity="o.entity"><span class="shrink-0 text-xs text-muted-foreground tabular-nums" :title="`owns ${o.count} pages here`">{{ o.count }}</span></EntityLink>
              </li>
            </ul>
          </SectionCard>

          <SectionCard :title="t.knowledge.lineageRecent" :icon="Activity" :to="`/knowledge/changes`" :link-label="t.knowledge.changeLog">
            <ListSkeleton v-if="changes.isPending.value" :rows="3" avatar />
            <ErrorState v-else-if="changes.error.value" compact :error="changes.error.value" @retry="changes.refetch()" />
            <p v-else-if="!changes.data.value?.length" class="text-sm text-muted-foreground">{{ t.knowledge.recentlyChangedEmpty }}</p>
            <ol v-else class="-my-1 divide-y">
              <li v-for="cs in changes.data.value" :key="cs.changeSetId"><ChangeFeedItem :change="cs" /></li>
            </ol>
          </SectionCard>

          <SectionCard v-if="data.mentions.length" :title="t.knowledge.lineageDocuments" :icon="FileText">
            <ul class="flex flex-col gap-2">
              <li v-for="m in data.mentions" :key="m.documentId">
                <RouterLink :to="`/k/doc/${encodeURIComponent(m.documentId)}`" class="-mx-1.5 flex flex-col gap-0.5 rounded px-1.5 py-1 hover:bg-muted">
                  <span class="flex items-center gap-2 text-sm font-medium"><FileText class="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" /> <span class="truncate">{{ m.title }}</span></span>
                  <span class="line-clamp-2 text-xs text-muted-foreground">{{ m.snippet }}</span>
                </RouterLink>
              </li>
            </ul>
          </SectionCard>
        </div>
      </div>
    </template>
  </div>
</template>
